import { deriveInclusionsExclusions, dropCoveredFdExclusions } from '@utazzo/shared';
import { Check, X } from 'lucide-react';
import React from 'react';

import { useItineraryComponents, useItineraryDetail } from '../use-itineraries';

import { useDestination } from '@/features/destinations/use-destinations';
import { useLead } from '@/features/leads/use-leads';

/**
 * Inclusions / Exclusions / Cancellation tab.
 *
 * Inclusions + exclusions are now AUTO-DERIVED from the components the itinerary
 * actually contains (flights, hotel, activities, transfers, visa, meals,
 * insurance) - what's present is an inclusion, what's absent an exclusion. There
 * is deliberately no add/edit box for the agent; the lists stay in sync with the
 * plan. Terms & cancellation are shown read-only (destination-wise content is a
 * later step). Nothing here is stored - it's computed on the fly.
 */

/** Standard cancellation policy shown until destination-wise policies land. */
const DEFAULT_CANCELLATION = [
  'More than 30 days before travel: 25% of the tour cost.',
  'Between 30 and 15 days before travel: 50% of the tour cost.',
  'Between 15 and 07 days before travel: 75% of the tour cost.',
  'Less than 07 days before travel or no-show: 100% of the tour cost.',
  'Flight, visa and non-refundable bookings are charged as per supplier rules.',
];

export const InclusionsTab = ({
  itineraryId,
  structureLocked = false,
}: {
  itineraryId: string;
  // Kept for a stable call signature; inclusions are auto-derived, never edited.
  editable?: boolean;
  /**
   * Fixed departures (ADR-0069): the vendor's own inclusion lines are the
   * contract - they say what the bundled land price covers. They lead, and the
   * derived lines (flights, visa, insurance) are appended after.
   */
  structureLocked?: boolean;
}): React.JSX.Element => {
  const detail = useItineraryDetail(itineraryId).data ?? null;
  const components = useItineraryComponents(itineraryId).data ?? [];
  const lead = useLead(detail?.leadId ?? undefined).data ?? null;
  const dest = useDestination(lead?.destinationCity?.id ?? undefined).data ?? null;
  const isDomesticTrip = dest?.isInternational === false;

  const { inclusions: derivedInclusions, exclusions: derivedExclusions } =
    deriveInclusionsExclusions(
      components.map((c) => ({
        kind: c.kind,
        title: c.title,
        fromLocation: c.fromLocation,
        toLocation: c.toLocation,
        visaId: c.visaId,
        mealServiceId: c.mealServiceId,
        // CRUISE rows carry shipName/nights/boardBasis for the cruise inclusion
        // line; ignored for every other kind.
        metaJson: c.metaJson,
      })),
      {
        numberOfNights: detail?.numberOfNights ?? 0,
        visaId: detail?.visaId ?? null,
        // ADR-0062. Passing the picked visa's processing mode so the derived
        // inclusion line names it (e.g. "Visa (appointment + embassy visit)")
        // and the embassy-fees exclusion appears when applicable.
        visaProcessingMode: detail?.visaProcessingMode ?? null,
        insuranceWanted: detail?.insuranceWanted ?? false,
        isDomestic: isDomesticTrip,
      },
      // FD (structureLocked): bake transfers/activities/flights/visa into the
      // day briefs + curated template, not components, so the derived set drops
      // transfer/activity/hotel lines and reads the curated inclusions as the
      // flight/visa/insurance "included" signal. Mirrors publish.service so this
      // tab shows EXACTLY what the share link publishes. FIT uses the defaults.
      structureLocked ? { format: 'FD', curatedInclusions: detail?.inclusions ?? [] } : {},
    );

  /**
   * A fixed departure carries the template's curated inclusions/exclusions AND
   * the lines that DERIVE from what the agent built on the itinerary (flights
   * point-to-point, visa, insurance, hotel, transfers). Merge the two, deduped,
   * so this tab shows EXACTLY what the publishable share link shows - the share
   * page does the same merge in publish.service (buildSnapshot). FIT keeps the
   * derived-only list.
   */
  const inclusions = structureLocked
    ? Array.from(new Set([...(detail?.inclusions ?? []), ...derivedInclusions]))
    : derivedInclusions;
  const exclusions = structureLocked
    ? Array.from(
        new Set([
          ...dropCoveredFdExclusions(detail?.exclusions ?? [], components),
          ...derivedExclusions,
        ]),
      )
    : derivedExclusions;

  const cancellation = detail?.cancellationPolicyHtml?.trim() ? null : DEFAULT_CANCELLATION;

  return (
    <div className="space-y-6 pb-20">
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <ReadOnlyList title="Inclusions" items={inclusions} tone="emerald" />
        <ReadOnlyList title="Exclusions" items={exclusions} tone="red" />
      </div>

      <section className="rounded-lg border border-zinc-200 bg-white">
        <header className="border-b border-zinc-100 px-5 py-3.5">
          <h3 className="text-sm font-semibold tracking-tight text-zinc-900">
            Cancellation policy
          </h3>
        </header>
        <div className="px-5 py-5">
          {cancellation ? (
            <ul className="space-y-2">
              {cancellation.map((line, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-300" />
                  {line}
                </li>
              ))}
            </ul>
          ) : (
            <div
              className="prose prose-sm max-w-none text-zinc-700"
              // Stored rich-text policy, if one was set previously.
              dangerouslySetInnerHTML={{ __html: detail?.cancellationPolicyHtml ?? '' }}
            />
          )}
        </div>
      </section>
    </div>
  );
};

const ReadOnlyList = ({
  title,
  items,
  tone,
}: {
  title: string;
  items: string[];
  tone: 'emerald' | 'red';
}): React.JSX.Element => {
  const dot = tone === 'emerald' ? 'text-emerald-500' : 'text-red-500';
  return (
    <section className="rounded-lg border border-zinc-200 bg-white">
      <header className="border-b border-zinc-100 px-5 py-3.5">
        <h3 className="text-sm font-semibold tracking-tight text-zinc-900">{title}</h3>
      </header>
      <div className="space-y-2 px-5 py-5">
        {items.length === 0 && <p className="text-xs text-zinc-400">Nothing yet.</p>}
        {items.map((item, i) => (
          <div key={i} className="flex items-start gap-2 text-sm text-zinc-700">
            {tone === 'emerald' ? (
              <Check className={'mt-0.5 h-3.5 w-3.5 shrink-0 ' + dot} aria-hidden />
            ) : (
              <X className={'mt-0.5 h-3.5 w-3.5 shrink-0 ' + dot} aria-hidden />
            )}
            <span>{item}</span>
          </div>
        ))}
      </div>
    </section>
  );
};
